import React, { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { avatarColor } from "@/lib/colors";
import { Kicker, LoadingCard, ErrorCard, EmptyState, RolePill } from "@/components/common";
import { Building2, Shield, User as UserIcon, Copy, Check } from "lucide-react";

export default function Team() {
  const { user, organization } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/org/members");
      setMembers(res.data.members || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setRole = async (memberId, role) => {
    setBusy(memberId);
    try {
      const res = await api.patch(`/org/members/${memberId}/role`, { role });
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role: res.data.role } : m)));
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || "Couldn't change role");
    } finally {
      setBusy(null);
    }
  };

  const copyDomain = async () => {
    try {
      await navigator.clipboard.writeText(`@${organization?.domain || ""}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };

  if (loading)
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <LoadingCard />
      </div>
    );
  if (error)
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <ErrorCard message={error} onRetry={load} />
      </div>
    );

  const managers = members.filter((m) => m.role === "manager").length;
  const employees = members.length - managers;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 animate-fade-up">
      <Kicker>Workspace</Kicker>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-zinc-950 mt-1 mb-6">
        Your team
      </h1>

      {/* Org card */}
      <div data-testid="org-card" className="rounded-xl border border-zinc-200 bg-white p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <div>
              <div className="font-display text-xl font-semibold text-zinc-950" data-testid="org-name">
                {organization?.name || "Workspace"}
              </div>
              <div className="text-sm text-zinc-500">
                Domain: <span className="font-mono text-zinc-700">{organization?.domain}</span>
              </div>
            </div>
          </div>
          <button
            data-testid="org-copy-domain-btn"
            onClick={copyDomain}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied!" : `Copy @${organization?.domain || ""}`}
          </button>
        </div>

        <div className="mt-5 pt-5 border-t border-zinc-100 grid grid-cols-3 gap-4 text-sm">
          <Stat icon={UserIcon} label="Total members" value={members.length} />
          <Stat icon={Shield} label="Managers" value={managers} />
          <Stat icon={UserIcon} label="Employees" value={employees} />
        </div>

        <div className="mt-5 rounded-lg border border-indigo-100 bg-indigo-50 text-indigo-800 text-xs px-3 py-2">
          <strong className="font-semibold">How to invite teammates:</strong> share Ledgerly with anyone who has an{" "}
          <span className="font-mono">@{organization?.domain}</span> email — they'll auto-join your workspace as
          employees when they register.
        </div>
      </div>

      {/* Members table */}
      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-zinc-100">
          <Kicker>Members</Kicker>
          <h3 className="font-display text-lg font-medium text-zinc-950 mt-0.5">
            {members.length} {members.length === 1 ? "person" : "people"}
          </h3>
        </div>
        {members.length === 0 ? (
          <div className="p-6">
            <EmptyState title="No teammates yet" description="Invite teammates with your domain email to get started." />
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 text-xs uppercase tracking-wider">
                <th className="px-6 py-3 font-medium">Member</th>
                <th className="px-6 py-3 font-medium">Email</th>
                <th className="px-6 py-3 font-medium">Joined</th>
                <th className="px-6 py-3 font-medium">Role</th>
                <th className="px-6 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const initials = (m.name || "?")
                  .split(" ")
                  .map((s) => s[0])
                  .slice(0, 2)
                  .join("")
                  .toUpperCase();
                const av = avatarColor(m.id);
                const isSelf = m.id === user?.id;
                return (
                  <tr key={m.id} data-testid={`member-row-${m.id}`} className="border-t border-zinc-100">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full ${av} text-white flex items-center justify-center text-xs font-semibold`}>
                          {initials}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-zinc-950">
                            {m.name} {isSelf ? <span className="text-zinc-400 font-normal">(you)</span> : null}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-zinc-700">{m.email}</td>
                    <td className="px-6 py-4 text-zinc-700">{m.created_at ? formatDate(m.created_at) : "—"}</td>
                    <td className="px-6 py-4">
                      <RolePill role={m.role} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      {m.role === "employee" ? (
                        <button
                          data-testid={`promote-${m.id}-btn`}
                          disabled={busy === m.id}
                          onClick={() => setRole(m.id, "manager")}
                          className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
                        >
                          <Shield className="w-3.5 h-3.5" /> Promote to manager
                        </button>
                      ) : (
                        <button
                          data-testid={`demote-${m.id}-btn`}
                          disabled={busy === m.id}
                          onClick={() => setRole(m.id, "employee")}
                          className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 disabled:opacity-60"
                        >
                          <UserIcon className="w-3.5 h-3.5" /> Demote to employee
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-zinc-100 text-zinc-700 flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
        <div className="font-display text-lg font-semibold text-zinc-950">{value}</div>
      </div>
    </div>
  );
}
